import { useFeiertagskonto } from '@/domain/mitarbeiter/features/feiertagskonto/hooks';
import {
  Button,
  Card,
  CardContent,
  EmptyState,
  Heading,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@e2n/ui/v2';
import {
  faMessageLines,
  faCalendarCirclePlus,
} from '@fortawesome/pro-regular-svg-icons';
import {
  DeleteKonotausgleichDialog,
  KontausgleichArtBadge,
  KontoausgleichHinzufügen,
} from '@/domain/mitarbeiter/features/feiertagskonto/components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useYearPickerContext } from '@/domain/mitarbeiter/features/feiertagskonto/shared/YearPickerContext';
import { useStandortContext } from '@/features/standortContext/StandortContext';

export function KontoausgleichDesktop() {
  const { t } = useTranslation();
  const { id } = useParams();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { zeitraum, year } = useYearPickerContext();
  const { feiertagskonto } = useFeiertagskonto({
    mitarbeiterId: id,
    zeitraum: zeitraum,
    standortId: standortId,
  });

  return (
    <div className="flex flex-col gap-2">
      <Heading className="flex flex-row justify-between gap-3">
        <div>{t('domain.mitarbeiter.feiertagskonto.kontoausgleich.titel')}</div>
        <KontoausgleichHinzufügen />
      </Heading>
      <Card variant="default">
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-60">
                  {t('domain.mitarbeiter.feiertagskonto.kontoausgleich.datum')}
                </TableHead>
                <TableHead className="w-72">
                  {t('domain.mitarbeiter.feiertagskonto.kontoausgleich.art')}
                </TableHead>
                <TableHead className="w-96">
                  {t('domain.mitarbeiter.feiertagskonto.kontoausgleich.tage')}
                </TableHead>
                <TableHead className="w-21"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {feiertagskonto.kontoausgleiche.length > 1 ? (
                feiertagskonto.kontoausgleiche.map((row, i) => (
                  <TableRow
                    key={i}
                    title={t(
                      'domain.mitarbeiter.feiertagskonto.kontoausgleich.datum',
                    )}>
                    <TableCell
                      type="text"
                      text={dayjs(row.datum).format('dd., DD.MM')}
                      showBottomLine={
                        i < feiertagskonto.kontoausgleiche.length - 1
                      }
                    />
                    <TableCell
                      type="text"
                      text={<KontausgleichArtBadge art={row.art} />}
                      showBottomLine={
                        i < feiertagskonto.kontoausgleiche.length - 1
                      }
                    />
                    <TableCell
                      type="text"
                      text={row.tage.toLocaleString('de-DE')}
                      showBottomLine={
                        i < feiertagskonto.kontoausgleiche.length - 1
                      }
                    />
                    <TableCell
                      type="actions"
                      showBottomLine={
                        i < feiertagskonto.kontoausgleiche.length - 1
                      }>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            iconOnly
                            aria-label="Bearbeiten"
                            size="small">
                            <FontAwesomeIcon icon={faMessageLines} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{row.bemerkung}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <DeleteKonotausgleichDialog
                            kontoausgleichId={id}
                            datum={row.datum}
                            art={row.art}
                          />
                        </TooltipTrigger>
                        <TooltipContent>{t('actions.löschen')}</TooltipContent>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow className="h-64">
                  <TableCell
                    className="text-center"
                    type="text"
                    text={
                      <EmptyState
                        description={t(
                          'domain.mitarbeiter.feiertagskonto.kontoausgleich.noDataDescription',
                          { year: dayjs(year).year() },
                        )}
                        icon={
                          <FontAwesomeIcon
                            icon={faCalendarCirclePlus}
                            className="text-neutrals-700"
                          />
                        }
                        title={t(
                          'domain.mitarbeiter.feiertagskonto.kontoausgleich.noDataTitle',
                        )}
                      />
                    }
                    colSpan={4}
                    showBottomLine={false}
                  />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
